import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import Navbar from './components/layout/Navbar';
import Dashboard from './components/dashboard/Dashboard';
import Login from './components/auth/Login';
import Register from './pages/Register';
import SystemAdminDashboard from './components/admin/SystemAdminDashboard';
import { initializeSystemSettings } from './firebase/collections/systemSettings';
import { RequireAuth } from './components/auth/RequireAuth';
import UserProfile from './components/profile/UserProfile';
import ImportPage from './pages/admin/ImportPage';
import CustomersPage from './pages/Customers';
import CustomerDetails from './components/customers/CustomerDetails';
import { LanguageProvider } from './contexts/LanguageContext';
import TicketsPage from './pages/TicketsPage';
import NewTicket from './components/tickets/NewTicket';
import TicketDetails from './components/tickets/TicketDetails';
import ReferralManager from './components/admin/ReferralManager';
import { Toaster } from 'react-hot-toast';
import ReferralTreeEditor from './components/admin/ReferralTreeEditor';
import UserDashboard from './components/user/Dashboard';
import ProjectManager from './components/admin/ProjectManager';
import DiscountManager from './components/admin/DiscountManager';
import WalletUserManager from './components/admin/WalletUserManager';
import WalletTransactionManager from './components/admin/WalletTransactionManager';
import WalletTransferManager from './components/admin/WalletTransferManager';
import AdminLogViewer from './components/admin/AdminLogViewer';
import DiscountCategoriesManager from './components/admin/DiscountCategoriesManager';
import Landing from './components/landing/Landing';
import EmailSender from './components/admin/EmailSender';
import EmailTemplateManager from './components/admin/EmailTemplateManager';
import UserGroupManager from './components/admin/UserGroupManager';
import SystemAdminManager from './components/admin/SystemAdminManager';
import WalletConnectionPage from './pages/WalletConnectionPage';
import WalletConnectionsPage from './pages/admin/WalletConnectionsPage';

function App() {
  useEffect(() => {
    initializeSystemSettings().catch(console.error);
  }, []);

  return (
    <LanguageProvider>
      <AuthProvider>
        <div className="min-h-screen bg-gray-100">
          <Navbar />
          <Toaster />
          <Routes>
            {/* Public Routes */}
            <Route 
              path="/" 
              element={
                <RequireAuth fallback={<Landing />}>
                  <Navigate to="/dashboard" replace />
                </RequireAuth>
              } 
            />
            <Route 
              path="/landing" 
              element={<Landing />} 
            />
            <Route 
              path="/login" 
              element={<Login />} 
            />
            <Route 
              path="/register/:referralCode" 
              element={<Register />} 
            />
            <Route 
              path="/register" 
              element={<Register />} 
            />
            
            {/* User Routes */}
            <Route path="/dashboard" element={
              <RequireAuth>
                <UserDashboard />
              </RequireAuth>
            } />
            <Route path="/profile" element={
              <RequireAuth>
                <UserProfile />
              </RequireAuth>
            } />
            <Route path="/wallet-connection" element={
              <RequireAuth adminOnly>
                <WalletConnectionPage />
              </RequireAuth>
            } />

            {/* Admin Routes */}
            <Route path="/admin/*" element={
              <RequireAuth adminOnly>
                <SystemAdminDashboard />
              </RequireAuth>
            } />
            <Route 
              path="/admin/customers" 
              element={
                <RequireAuth adminOnly>
                  <CustomersPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/system"
              element={
                <RequireAuth adminOnly>
                  <SystemAdminDashboard />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/tickets"
              element={
                <RequireAuth adminOnly>
                  <TicketsPage />
                </RequireAuth>
              }
            />
            <Route 
              path="/admin/tickets/new" 
              element={
                <RequireAuth adminOnly>
                  <NewTicket />
                </RequireAuth>
              }
            />
            <Route 
              path="/admin/tickets/:ticketId" 
              element={
                <RequireAuth adminOnly>
                  <TicketDetails />
                </RequireAuth>
              }
            />
            <Route 
              path="/admin/customers/:customerId" 
              element={
                <RequireAuth adminOnly>
                  <CustomerDetails />
                </RequireAuth>
              }
            />
            <Route 
              path="/admin/import" 
              element={
                <RequireAuth adminOnly>
                  <ImportPage />
                </RequireAuth>
              }
            />
            <Route 
              path="/admin/referrals" 
              element={
                <RequireAuth adminOnly>
                  <ReferralManager />
                </RequireAuth>
              }
            />
            <Route 
              path="/admin/referrals/tree" 
              element={
                <RequireAuth adminOnly>
                  <ReferralTreeEditor />
                </RequireAuth>
              }
            />
            <Route 
              path="/admin/projects" 
              element={
                <RequireAuth adminOnly>
                  <ProjectManager />
                </RequireAuth>
              }
            />
            <Route path="/admin/discounts" element={<DiscountManager />} />
            <Route path="/admin/discount-categories" element={<DiscountCategoriesManager />} />
            <Route 
              path="/admin/wallet-users" 
              element={
                <RequireAuth adminOnly>
                  <WalletUserManager />
                </RequireAuth>
              }
            />
            <Route 
              path="/admin/wallet-transactions" 
              element={
                <RequireAuth adminOnly>
                  <WalletTransactionManager />
                </RequireAuth>
              }
            />
            <Route 
              path="/admin/wallet-transfers" 
              element={
                <RequireAuth adminOnly>
                  <WalletTransferManager />
                </RequireAuth>
              }
            />
            <Route 
              path="/admin/logs" 
              element={
                <RequireAuth adminOnly>
                  <AdminLogViewer />
                </RequireAuth>
              }
            />
            <Route 
              path="/admin/email" 
              element={
                <RequireAuth adminOnly>
                  <EmailSender />
                </RequireAuth>
              }
            />
            <Route 
              path="/admin/email-templates" 
              element={
                <RequireAuth adminOnly>
                  <EmailTemplateManager />
                </RequireAuth>
              }
            />
            <Route 
              path="/admin/user-groups" 
              element={
                <RequireAuth adminOnly>
                  <UserGroupManager />
                </RequireAuth>
              }
            />
            <Route 
              path="/admin/system-admins" 
              element={
                <RequireAuth adminOnly>
                  <SystemAdminManager />
                </RequireAuth>
              }
            />
            <Route 
              path="/admin/wallet-connections" 
              element={
                <RequireAuth adminOnly>
                  <WalletConnectionsPage />
                </RequireAuth>
              }
            />
          </Routes>
        </div>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
